// BSLMC CVOR Bivalirudin Protocol (last updated January 2021), restructured for
// the in-app guide. Source PDF: public/thi/thi-bivalirudin-protocol.pdf

export const BIVAL_PDF = '/thi/thi-bivalirudin-protocol.pdf';
export const BIVAL_UPDATED = 'January 2021';

/** Two dosing modes. Rates in mg/kg (bolus) and mg/kg/hr (infusion). */
export const MODES = [
  {
    id: 'on-pump',
    label: 'On-pump CPB',
    bolus: 1,
    infusion: 2.5,
    prime: 50,
    actTarget: { kind: 'baseline', factor: 2.5, text: '≥ 2.5× baseline ACT' },
  },
  {
    id: 'off-pump',
    label: 'Off-pump · TAVR · PCI',
    bolus: 0.75,
    infusion: 1.75,
    prime: null,
    actTarget: { kind: 'fixed', seconds: 300, text: '≥ 300 seconds' },
  },
];

/** Infusion rate by renal function, per mode (mg/kg/hr), from the protocol tables */
export const RENAL = [
  { id: 'normal', label: 'GFR ≥ 60', rate: { 'on-pump': 2.5, 'off-pump': 1.75 }, note: 'No adjustment' },
  { id: 'moderate', label: '30–59', rate: { 'on-pump': 2, 'off-pump': 1.5 }, note: '20% reduction' },
  { id: 'severe', label: '10–29', rate: { 'on-pump': 1, 'off-pump': 0.75 }, note: '60% reduction', warn: 'Strongly consider argatroban instead' },
  { id: 'dialysis', label: 'Dialysis', rate: { 'on-pump': 0.25, 'off-pump': 0.2 }, note: '90% reduction', warn: 'Strongly consider argatroban instead' },
];

export const EXTRA_BOLUS = { min: 0.1, max: 0.5 }; // mg/kg, for a sub-therapeutic ACT
export const INFUSION_STEP = { min: 0.25, max: 0.5 }; // mg/kg/hr, up or down

/** Step-by-step sections, keyed by mode. Each: { title, groups: [{ title?, items[] }] } */
export const STEPS = {
  'on-pump': [
    {
      title: 'Before bypass',
      groups: [
        { items: [
          'Confirm the need: positive SRA and/or positive heparin sensitivity',
          'Check a baseline ACT',
          'Bolus 1 mg/kg, then start the infusion at 2.5 mg/kg/hr (adjusted for renal function)',
          'Add 50 mg to the CPB prime',
          'Check the ACT 5 minutes after the bolus. Target ≥ 2.5× baseline',
          'Sub-therapeutic: additional bolus 0.1–0.5 mg/kg',
        ] },
      ],
    },
    {
      title: 'On bypass',
      groups: [
        { title: 'Anticoagulation', items: [
          'Check the ACT every 15–30 minutes',
          'Sub-therapeutic: bolus 0.1–0.5 mg/kg and increase the infusion by 0.25–0.5 mg/kg/hr',
          'Supra-therapeutic: consider decreasing the infusion by 0.25–0.5 mg/kg/hr',
        ] },
        { title: 'Surgical field', items: [
          'Eliminate areas of blood stasis in the field and the circuit. Bivalirudin keeps being metabolised in pooled blood, so clots can form there',
          'Flush blood-filled lines and CPB lines every 15 minutes. Flush solution: bivalirudin 0.1 mg/mL',
          'Clots in stagnant blood do not necessarily mean inadequate anticoagulation',
          'LIMA: open the bulldog clamp every 15 minutes',
          'Vein grafts: saline flush (or bivalirudin 0.1 mg/mL) may be preferred to blood',
          'Minimise hypothermia. Metabolism is temperature-dependent, and cooling raises bleeding risk',
          'Prefer Cell Saver suction over the pump cardiotomy suction',
        ] },
        { title: 'Perfusion', items: [
          'Use the larger pump (Medtronic Fusion), which has a continuous purge',
          'Retrograde autologous prime is fine as long as bypass starts promptly',
          'If blood is added to the prime, add it immediately before going on',
          'Flush all blood-filled lines every 15 minutes, including cardioplegia',
          'Keep the venous reservoir at or below 1000–1200 mL. Park extra volume in CPD bags',
          'Hemofiltration is not recommended because it removes bivalirudin. If used, watch the ACT closely',
          'Cardioplegia: consider Buckberg. With del Nido, crystalloid may be preferred to blood',
        ] },
      ],
    },
    {
      title: 'Coming off bypass',
      groups: [
        { items: [
          'Stop the infusion at least 15 minutes (15–30) before anticipated separation',
          'Consider ultrafiltration in the last 30–60 minutes to clear bivalirudin',
          'If bypass is not terminated in that window: bolus 0.5 mg/kg and restart the infusion at 2.5 mg/kg/hr',
        ] },
      ],
    },
    {
      title: 'After bypass',
      groups: [
        { title: 'Hemostasis', items: [
          'Check ACT, aPTT, PT/INR, platelets, fibrinogen, Hb/Hct and/or TEG after separation and as needed',
          'Correct surgical causes of bleeding',
          'Transfuse to targets: Hb > 7.5 · INR < 1.8 · platelets > 75k · fibrinogen > 250',
        ] },
        { title: 'Temperature', items: [
          'Hypothermia slows bivalirudin metabolism. Keep the patient warm',
        ] },
        { title: 'Cell Saver', items: [
          'A single wash removes 90% of bivalirudin. Consider washing twice before reinfusing',
        ] },
        { title: 'Circuit', items: [
          'Keep the circuit ready to return to bypass: connect arterial and venous lines in a closed loop (or bridge) and maintain flow',
          'Add 50 mg bivalirudin to the circuit after separation',
        ] },
      ],
    },
  ],
  'off-pump': [
    {
      title: 'Anticoagulation',
      groups: [
        { items: [
          'Confirm the need: positive SRA and/or positive heparin sensitivity',
          'Check a baseline ACT',
          'Bolus 0.75 mg/kg, then start the infusion at 1.75 mg/kg/hr (adjusted for renal function)',
          'Check the ACT 5 minutes after the bolus. Target ≥ 300 seconds',
          'Sub-therapeutic: additional bolus 0.1–0.5 mg/kg',
        ] },
      ],
    },
    {
      title: 'If plans change',
      groups: [
        { title: 'Converting to CPB', items: [
          'Bolus 0.25 mg/kg',
          'Increase the infusion to 2.5 mg/kg/hr',
          'Add 50 mg to the CPB prime',
        ] },
        { title: 'Going onto ECMO', items: [
          'No extra bivalirudin is needed if the ACT is therapeutic',
        ] },
      ],
    },
  ],
};

export const RESCUE = {
  title: 'Severe refractory bleeding',
  items: [
    'Continue balanced transfusion of FFP, cryoprecipitate and platelets, guided by labs',
    'DDAVP 0.3 mcg/kg',
    'Activated factor VII: start with 1–2 mg, maximum 90 mcg/kg',
    'Consider CRRT, which removes up to 25% of circulating bivalirudin',
    'Consider a hematopathology consult',
    'Consider packing the chest and leaving it open. Bivalirudin is negligible within 12–24 hours',
  ],
  warning: 'KCentra contains heparin and is contraindicated in HIT.',
};

export const DRUG_FACTS = [
  { title: 'Mixing', items: [
    'Pharmacy stocks 250 mg vials of lyophilised bivalirudin',
    'Reconstitute and add to 250 mL of saline or D5W for 1 mg/mL',
  ] },
  { title: 'Run on a separate line from', items: [
    'Vancomycin',
    'Amiodarone',
    'Dobutamine above 4 mg/mL (our standard concentration is exactly 4 mg/mL)',
  ] },
  { title: 'Monitoring', items: [
    'The ecarin clotting time is ideal but not clinically available. The ACT is acceptable, with a looser correlation',
    'Bivalirudin prolongs the aPTT, PT/INR, thrombin time and ACT',
  ] },
  { title: 'Key properties', items: [
    'No reversal agent',
    'Cleared 80% by enzymatic cleavage and 20% renally',
    'Half-life 25 minutes with normal renal function, up to 210 minutes on dialysis',
    'Associated with more post-bypass bleeding than heparin',
  ] },
];

export const ALTERNATIVES = {
  title: 'Alternatives in HIT',
  items: [
    'If the case can wait, let the anti-heparin-PF4 antibodies clear (40–100 days) and use heparin',
    'Plasma exchange before or during surgery to remove the antibodies, then heparin',
    'In renal failure, consider argatroban instead of bivalirudin',
  ],
};

export const BACKGROUND = [
  'Patients with heparin hypersensitivity or heparin-induced thrombocytopenia (HIT) who need cardiopulmonary bypass require an alternative anticoagulant. Bivalirudin, a direct thrombin inhibitor, is the only heparin alternative evaluated in prospective studies for bypass. It is not FDA-approved for cardiac surgery, but it has the broadest experience and is recommended by the STS, SCA and AmSECT guidelines.',
  'Compared with heparin, bivalirudin has a linear, predictable dose-response, needs no cofactor, has very low immunogenicity, does not activate platelets, binds both clot-bound and free fibrin, and may improve graft patency after CABG.',
  'Elimination is mostly enzymatic (80%) with the remainder renal. Renal dysfunction prolongs the half-life considerably, which drives the infusion adjustments above.',
];

export const PK_TABLE = [
  { fn: 'Normal (GFR ≥ 90)', clearance: '3.4', half: '25 min' },
  { fn: 'Mild (60–89)', clearance: '3.4', half: '22 min' },
  { fn: 'Moderate (30–59)', clearance: '2.7', half: '34 min' },
  { fn: 'Severe (10–29)', clearance: '2.8', half: '57 min' },
  { fn: 'Dialysis-dependent', clearance: '1.0', half: '3.5 hours' },
];

export const REFERENCES = [
  'Shore-Lesserson L, Baker RA, Ferraris VA, et al. STS/SCA/AmSECT Clinical Practice Guidelines: Anticoagulation During Cardiopulmonary Bypass. Ann Thorac Surg 2018;105:650-662.',
  'Cuker A, Arepally GM, Chong BH, et al. American Society of Hematology 2018 guidelines for management of venous thromboembolism: heparin-induced thrombocytopenia. Blood Adv 2018;2:3360-3392.',
  'Warkentin TE, Koster A. Bivalirudin: a review. Expert Opin Pharmacother 2005;6:1349-1371.',
  'Bivalirudin [package insert]. Parsippany, NJ: The Medicines Company; 2016.',
  'Dyke CM, Smedira NG, Koster A, et al. A comparison of bivalirudin to heparin with protamine reversal in patients undergoing cardiac surgery with cardiopulmonary bypass: the EVOLUTION-ON study. J Thorac Cardiovasc Surg 2006;131:533-9.',
  'Koster A, Dyke CM, Aldea G, et al. Bivalirudin during cardiopulmonary bypass in patients with previous or acute heparin-induced thrombocytopenia and heparin antibodies: results of the CHOOSE-ON trial. Ann Thorac Surg 2007;83:572-7.',
  'Stratmann G, DeSilva AM, Tseng EE, et al. Reversal of direct thrombin inhibition after cardiopulmonary bypass in a patient with heparin-induced thrombocytopenia. Anesth Analg 2004;98:1635-1639.',
];
